import React, { useState, useRef } from 'react';
import play from "../../../../assets/play.svg";
import stop from "../../../../assets/stop.svg";

interface AudioPlayerProps {
    src: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const handlePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (audioRef.current) {
            const newTime = parseFloat(e.target.value);
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const formatTime = (time: number) => {
        if (!isFinite(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    return (
        <div
            className="relative max-w-[90vw] max-h-[90vh] bg-neutral-300 dark:bg-neutral-900 p-8 rounded-xl"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex flex-col gap-4 mt-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={handlePlayPause}
                        className="p-2"
                    >
                        <img
                            src={isPlaying ? stop : play}
                            alt={isPlaying ? "Stop" : "Play"}
                            className={`w-6 h-6 ${!isPlaying ? "invert dark:invert-0" : ""} opacity-75 hover:opacity-100`}
                        />
                    </button>
                    <div className="flex-1 flex items-center gap-4">
                        <span className="text-black dark:text-white text-sm opacity-75">{formatTime(currentTime)}</span>
                        <input
                            type="range"
                            min="0"
                            max={duration}
                            value={currentTime}
                            onChange={handleSeek}
                            className="w-full h-1 bg-neutral-700 rounded-full cursor-pointer accent-red-500"
                        />
                        <span className="text-black dark:text-white text-sm opacity-75">{formatTime(duration)}</span>
                    </div>
                </div>
                <audio
                    ref={audioRef}
                    src={src}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    className="hidden"
                />
            </div>
        </div>
    );
};

export default AudioPlayer;